import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ClubProvider } from "@/contexts/ClubContext";
import { I18nProvider, useI18n } from "@/contexts/I18nContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { useEffect } from "react";
import { localizeDomOnce, observeDomLocalization } from "@/lib/runtimeLocalizer";

import Login from "./pages/Login";
// import Signup from "./pages/Signup";
import FirstLogin from "./pages/FirstLogin";
import SelectClub from "./pages/SelectClub";
import DashboardLayout from "./components/layout/DashboardLayout";
import Dashboard from "./pages/dashboard/Dashboard";
import ClubManagement from "./pages/dashboard/ClubManagement";
import MemberManagement from "./pages/dashboard/MemberManagement";
import QueueAndCourts from "./pages/dashboard/QueueAndCourts";
import MatchHistory from "./pages/dashboard/MatchHistory";
import EquipmentManagement from "./pages/dashboard/EquipmentManagement";
import Evaluations from "./pages/dashboard/Evaluations";
import Profile from "./pages/dashboard/Profile";
import SettingsPage from "./pages/dashboard/Settings";
import Leaderboard from "./pages/dashboard/Leaderboard";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsConditions";
import ForgotPassword from "./pages/ForgotPassword";
import AppShellSkeleton from "./components/AppShellSkeleton";
import DailyMatchReport from "./pages/dashboard/DailyMatchReport";
import ConsentGate from "./pages/ConsentGate";
import SuperAdmin from "./pages/SuperAdmin";
import PackagesPage from "./pages/dashboard/Packages";
// import Home from "./pages/Home";

const queryClient = new QueryClient();

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <AppShellSkeleton />;
  }
  
  if (user) {
    return <Navigate to="/select-club" replace />;
  }
  
  return <>{children}</>;
}

function RouteLocalizer() {
  const { language } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const raf = requestAnimationFrame(() => localizeDomOnce(language));
    const timers = [120, 350, 800, 1500].map((ms) =>
      window.setTimeout(() => localizeDomOnce(language), ms),
    );
    const stopObserver = observeDomLocalization(language);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach((timer) => window.clearTimeout(timer));
      stopObserver();
    };
  }, [language, location.pathname, location.search, location.hash]);

  return null;
}

function RouteLanguageSwitcher() {
  const { language } = useI18n();
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const visiblePaths = new Set([
    "/dashboard",
    "/login",
    "/signup",
    "/first-login",
    "/forgot-password",
    "/privacy-policy",
    "/terms-and-conditions",
  ]);
  const show = visiblePaths.has(path);

  if (!show) return null;
  return (
    <div className={`fixed top-3 z-[80] ${language === 'ar' ? 'left-3' : 'right-3'}`}>
      <LanguageSwitcher />
    </div>
  );
}

function LocalizedDashboard() {
  const { language } = useI18n();
  return <Dashboard key={language} />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <I18nProvider>
        <BrowserRouter>
          <RouteLanguageSwitcher />
          <RouteLocalizer />
          <AuthProvider>
            <ClubProvider>
              <Routes>
              {/* <Route path="/" element={<Home />} /> */}
              {/* path / directly navigate to login */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/first-login" element={<FirstLogin />} />
              {/* <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} /> */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

              <Route element={<ProtectedRoute />}>
                <Route path="/consent" element={<ConsentGate />} />
                <Route path="/select-club" element={<SelectClub />} />
                <Route element={<ProtectedRoute requiresSuperAdmin />}>
                  <Route path="/super-admin" element={<SuperAdmin />} />
                </Route>

                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<LocalizedDashboard />} />

                  <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                    <Route path="club" element={<ClubManagement />} />
                  </Route>

                  <Route element={<ProtectedRoute allowedRoles={['admin']} requiresClub />}>
                    <Route path="members" element={<MemberManagement />} />
                  </Route>

                  <Route element={<ProtectedRoute allowedRoles={['admin', 'coach', 'member']} requiresClub />}>
                    <Route path="leaderboard" element={<Leaderboard />} />
                    <Route path="queue" element={<QueueAndCourts />} />
                    <Route path="equipment" element={<EquipmentManagement />} />
                    <Route path="history" element={<MatchHistory />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="packages" element={<PackagesPage />} />
                    <Route element={<ProtectedRoute allowedRoles={['admin']} requiresClub />}>
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>
                  </Route>

                  <Route element={<ProtectedRoute allowedRoles={['admin', 'coach']} requiresClub />}>
                    <Route path="evaluations" element={<Evaluations />} />
                    <Route path="daily-report" element={<DailyMatchReport />} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
              </Routes>
            </ClubProvider>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
